import './Select.scss';
import { SelectOption } from '@/core/types';
import {
  KeyboardEventHandler,
  MouseEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useOnClickOutside, useDebounce, useSearch } from '../../hooks';
import PolygonIcon from '@/icons/chevron-right.svg?react';
import PolygonSmIcon from '@/icons/select-chevron.svg?react';
import { useTranslation } from 'react-i18next';
import SimpleBar from 'simplebar-react';
import { SearchInput } from '../SearchInput/SearchInput';
import MiniLoader from '../MiniLoader/MiniLoader';

interface Props {
  options: SelectOption[];
  value?: number | string;
  onChange: (v: string | number) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  label?: string | null;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  isOpenAbove?: boolean;
  name?: string;
  size?: 'lg' | 'sm' | 'md';
  search?: boolean;
  triggerClassname?: string;
  arrowClassname?: string;
  isLoading?: boolean;
  styling?: 'default' | 'no_validation_description';
  allCountries?: SelectOption[];
}

const getActiveOption = (value: string | number, options: SelectOption[]) =>
  options.find((option) => value === option.id);

export const Select = ({
  options,
  value,
  placeholder = 'Please select',
  onChange,
  onBlur,
  className = '',
  triggerClassname = '',
  arrowClassname = '',
  name,
  label,
  error,
  touched,
  disabled = false,
  isOpenAbove,
  size = 'md',
  search,
  isLoading,
  styling = 'default',
  allCountries,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const sourceOptions = allCountries || options;
  const { found, onSearchChange, searchString } = useSearch<SelectOption>(
    sourceOptions,
    filterFn
  );

  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<any>(null);
  const ref = useRef<HTMLButtonElement>(null);
  const [isOpened, setIsOpened] = useState(false);
  const checkedOption = value
    ? getActiveOption(value, sourceOptions)
    : undefined;
  const debouncedIsOpened = useDebounce(isOpened, 250);
  const isDropdownOpened = isOpened || debouncedIsOpened;
  const [activeIndex, setActiveIndex] = useState(
    options.findIndex((o) => o.id === value)
  );
  const visibleOptions = useMemo(
    () => (search && !!searchString.trim().length ? found || [] : options),
    [search, searchString, found, options]
  );

  const content = checkedOption
    ? checkedOption.content || t(checkedOption.name)
    : t(placeholder);

  const onClickOuside = () => {
    if (!isOpened) return;
    setIsOpened(false);
    onBlur && onBlur();
  };

  useOnClickOutside(ref, onClickOuside);

  const onClick: MouseEventHandler<HTMLButtonElement> = () => {
    if (disabled) return;
    setIsOpened(!isOpened);
  };

  const handleBlur = () => {
    if (disabled) return;
    if (onBlur) onBlur();
  };

  const onSelect = (optionId: string | number) => () => {
    setIsOpened(false);
    onChange(optionId);
  };

  useEffect(() => {
    if (isOpened) {
      inputRef.current?.focus();
    }
  }, [isOpened]);

  useEffect(() => {
    if (isOpened && activeIndex !== -1 && menuRef.current) {
      menuRef.current.el.focus();
      const contentWrapper = menuRef.current.getContentElement();
      const targetEl = contentWrapper.children[activeIndex];
      const targetRect = targetEl?.getBoundingClientRect();
      const containerRect = menuRef.current.el
        .querySelector('.simplebar-content-wrapper')
        .getBoundingClientRect();

      if (
        targetRect &&
        containerRect &&
        activeIndex <= visibleOptions.length - 1 &&
        (targetRect.bottom > containerRect.bottom ||
          targetRect.top < containerRect.top)
      ) {
        targetEl.scrollIntoView();
      }
    }

    if (!isOpened) {
      setActiveIndex(-1);
    }
  }, [isOpened, activeIndex, visibleOptions]);

  const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (event) => {
    switch (event.key) {
      case ' ':
      case 'Enter':
        if (isOpened && activeIndex !== -1 && visibleOptions[activeIndex]?.id) {
          onSelect(visibleOptions[activeIndex].id)();
          break;
        }
        return;
      case 'Escape':
        if (isOpened) setIsOpened(false);
        break;
      case 'ArrowUp':
        if (isOpened) {
          if (activeIndex > 0) {
            setActiveIndex((s) => s - 1);
          } else {
            setActiveIndex(visibleOptions.length - 1);
          }
        }
        break;
      case 'ArrowDown':
        if (isOpened) {
          if (activeIndex !== -1 && activeIndex < visibleOptions.length - 1) {
            setActiveIndex((s) => s + 1);
          } else {
            setActiveIndex(0);
          }
        }
        break;

      default:
        return;
    }
    event.preventDefault();
  };

  return (
    <div
      className={classNames('Select', className, {
        'Select--lg': size === 'lg',
        'Select--sm': size === 'sm',
      })}
    >
      {!!label && <div className='Select__label'>{t(label)}</div>}
      <button
        data-name={name}
        data-value={value}
        data-testid={name}
        ref={ref}
        type='button'
        className={classNames(
          'Select__input flex items-center justify-between',
          triggerClassname,
          {
            'Select__input--active': isOpened,
            'Select__input--disabled': disabled,
            'Select__input--invalid': touched && error,
          }
        )}
        onClick={onClick}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
      >
        <span
          className={classNames({
            Select__value: value,
            Select__placeholder: !value,
          })}
        >
          {content}
        </span>
        {isLoading ? (
          <MiniLoader />
        ) : (
          <>
            {size === 'lg' ? (
              <PolygonIcon className={`Select__arrow ${arrowClassname}`} />
            ) : (
              <PolygonSmIcon className={`Select__arrow ${arrowClassname}`} />
            )}
          </>
        )}

        {isDropdownOpened && (
          <div
            className={classNames('Select__dropdown', {
              fadeIn: isOpened,
              fadeOut: !isOpened,
              'Select__dropdown--up': isOpenAbove,
            })}
            ref={dropdownRef}
          >
            {search && (
              <div onClick={(e) => e.stopPropagation()} className='p-1'>
                <SearchInput
                  ref={inputRef}
                  name='search'
                  onChange={onSearchChange}
                  value={searchString}
                  placeholder={t('Search')}
                />
              </div>
            )}
            <SimpleBar
              style={{ maxHeight: 220 }}
              ref={menuRef}
              autoHide={false}
              className='Select__scrollbar'
            >
              {visibleOptions.map((o, i) => (
                <div
                  style={{ position: 'relative', zIndex: 1000 }}
                  className={classNames('Select__dropdownItem text-start', {
                    'Select__dropdownItem--active': activeIndex === i,
                  })}
                  onClick={onSelect(o.id)}
                  key={o.id}
                >
                  {o.content || t(o.name)}
                </div>
              ))}
            </SimpleBar>
          </div>
        )}
      </button>
      {touched && error && styling !== 'no_validation_description' && (
        <div className='Select__error' data-testid={`${name}-error`}>
          {t(error)}
        </div>
      )}
    </div>
  );
};

const filterFn = (str: string) => (c: SelectOption) => {
  if (typeof c.name !== 'string') return true;
  return c.name.toLowerCase().includes(str.toLowerCase().trim());
};
